import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "@/components/Reveal";
import { MessageCircleHeart, ShieldCheck, Lock } from "lucide-react";

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const artY = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const tagY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section
      id="about"
      ref={ref}
      className="py-24 md:py-40 px-6 md:px-12"
      data-testid="about-section"
    >
      <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Text */}
        <div className="lg:col-span-6 order-2 lg:order-1">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.24em] font-semibold text-primary mb-6">
              What is KelCalm
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif font-light tracking-tight text-foreground text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-8 text-balance">
              A private line to real support — built for students, safeguarded by design.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-lg leading-relaxed text-muted-foreground max-w-xl">
              KelCalm is a secure digital mental health platform built for students. It
              connects you with trained counsellors through anonymous, moderated
              messaging — no names, no exposure, just a private line to real support.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 text-lg leading-relaxed text-muted-foreground max-w-xl">
              Every conversation is confidential, every interaction is safeguarded, and
              every student is met with care.
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap gap-x-12 gap-y-6">
              {[
                ["100%", "Anonymous by default"],
                ["24/7", "Support within reach"],
                ["0", "Names ever exposed"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-serif text-4xl text-primary">{n}</div>
                  <div className="text-sm text-muted-foreground mt-1 max-w-[9rem]">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Editorial art (no stock photography) */}
        <div className="lg:col-span-6 order-1 lg:order-2">
          <Reveal>
            <div className="relative">
              <div
                className="absolute -inset-4 rounded-[2rem] bg-accent/15 -rotate-2"
                aria-hidden
              />
              <motion.div
                style={{ y: artY }}
                className="relative overflow-hidden rounded-[2rem] aspect-[4/5] bg-gradient-to-br from-primary via-primary to-primary/80 shadow-lg"
              >
                {/* soft blobs */}
                <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-accent/40 blur-3xl" aria-hidden />
                <div className="absolute -bottom-24 -left-10 h-64 w-64 rounded-full bg-white/10 blur-3xl" aria-hidden />

                {/* grid */}
                <svg
                  className="absolute inset-0 h-full w-full opacity-[0.09]"
                  aria-hidden
                >
                  <defs>
                    <pattern id="g" width="28" height="28" patternUnits="userSpaceOnUse">
                      <path
                        d="M 28 0 L 0 0 0 28"
                        fill="none"
                        stroke="white"
                        strokeWidth="0.6"
                      />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#g)" />
                </svg>

                {/* conversation bubbles */}
                <div className="relative z-10 h-full w-full p-8 md:p-12 flex flex-col justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/15 backdrop-blur">
                      <Lock className="h-4 w-4 text-primary-foreground" />
                    </div>
                    <p className="text-xs uppercase tracking-[0.2em] text-primary-foreground/80 font-semibold">
                      Encrypted · Anonymous
                    </p>
                  </div>

                  <div className="space-y-3 max-w-[85%]">
                    <div className="inline-block rounded-2xl rounded-bl-md bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/15 px-4 py-3 text-primary-foreground/90 text-sm md:text-base">
                      I don&apos;t know how to say this out loud.
                    </div>
                    <div className="ml-auto block w-fit rounded-2xl rounded-br-md bg-accent text-accent-foreground px-4 py-3 text-sm md:text-base shadow-sm">
                      You don&apos;t have to. I&apos;m here — take your time.
                    </div>
                    <div className="inline-block rounded-2xl rounded-bl-md bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/15 px-4 py-3 text-primary-foreground/90 text-sm md:text-base">
                      And no one will ever know it&apos;s me?
                    </div>
                    <div className="ml-auto flex w-fit items-center gap-2 rounded-2xl rounded-br-md bg-accent text-accent-foreground px-4 py-3 text-sm md:text-base shadow-sm">
                      <ShieldCheck className="h-4 w-4" />
                      Never. That&apos;s a promise.
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageCircleHeart className="h-5 w-5 text-primary-foreground" />
                      <span className="font-serif italic text-primary-foreground text-lg">
                        A safe space, always open.
                      </span>
                    </div>
                    <div className="flex -space-x-1.5" aria-hidden>
                      <span className="h-2 w-2 rounded-full bg-primary-foreground/80 animate-pulse" />
                      <span
                        className="h-2 w-2 rounded-full bg-primary-foreground/60 animate-pulse"
                        style={{ animationDelay: "150ms" }}
                      />
                      <span
                        className="h-2 w-2 rounded-full bg-primary-foreground/40 animate-pulse"
                        style={{ animationDelay: "300ms" }}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                style={{ y: tagY }}
                className="absolute -bottom-6 -left-6 rounded-2xl bg-background border border-border shadow-md px-6 py-4 max-w-[16rem]"
              >
                <p className="font-serif italic text-lg text-foreground leading-snug">
                  “You&apos;re heard, not identified.”
                </p>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;
